/**
 * OmegaProductsPage — /omega/products
 *
 * The founder's product index: the GI products OMEGA takes to market (Monitor,
 * The Perfect Loop / Guardian Runtime). The live pricing tiers, prices, and
 * Stripe configuration status are read from the server-computed product catalog
 * (`/admin/products/catalog`, S54 §15.5). Each paid tier surfaces a
 * customer-driven "Checkout (TEST)" link into the existing Stripe subscribe
 * flow — there is NO autonomous charge. Founder-only; white cockpit theme;
 * loading / error / empty states so it never blanks out.
 */
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

interface CatalogTier {
  name: string;
  price_cents: number;
  interval: 'free' | 'month' | 'year';
  paid: boolean;
  checkout_path?: string;
}

interface CatalogProduct {
  slug: string;
  name: string;
  tagline: string;
  tiers: CatalogTier[];
}

interface CatalogResponse {
  stripe_status: 'live-ready' | 'test';
  stripe_configured: boolean;
  products: CatalogProduct[];
}

/** Format i64 USD cents as a price string. `0` → "Free". */
function formatPrice(cents: number, interval: CatalogTier['interval']): string {
  if (cents === 0) return 'Free';
  const dollars = (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (interval === 'month') return `${dollars}/mo`;
  if (interval === 'year') return `${dollars}/yr`;
  return dollars;
}

export function OmegaProductsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const catalog = useQuery({
    queryKey: ['products', 'catalog'],
    queryFn: () => apiFetch<CatalogResponse>('/admin/products/catalog'),
  });

  const stripeLive = catalog.data?.stripe_configured ?? false;
  const products = catalog.data?.products ?? [];

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', color: '#111827', padding: 24, maxWidth: 900 }}>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
        <Link to="/omega" style={{ color: '#2563eb' }}>← OMEGA</Link>
      </p>
      <h1 style={{ margin: '0 0 6px', fontSize: 22 }}>Products</h1>
      <p style={{ color: '#6b7280', marginBottom: 8 }}>
        Live pricing tiers for the GI products OMEGA takes to market.
      </p>

      {!catalog.isPending && !catalog.isError && (
        <p style={{ fontSize: 12, marginBottom: 20 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 999,
              fontWeight: 600,
              background: stripeLive ? '#ecfdf5' : '#fffbeb',
              color: stripeLive ? '#047857' : '#b45309',
              border: `1px solid ${stripeLive ? '#a7f3d0' : '#fde68a'}`,
            }}
          >
            Stripe: {stripeLive ? 'live-ready' : 'TEST'}
          </span>
        </p>
      )}

      {catalog.isPending ? (
        <p style={{ color: '#6b7280' }}>Loading product catalog…</p>
      ) : catalog.isError ? (
        <p style={{ color: '#b91c1c' }}>Product catalog unavailable.</p>
      ) : products.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No products in the catalog yet.</p>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {products.map((p) => (
            <div
              key={p.slug}
              style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 18 }}
            >
              <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>{p.name}</h2>
              <p style={{ margin: '0 0 12px', color: '#4b5563', fontSize: 14 }}>{p.tagline}</p>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>Tier</th>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>Price</th>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}>Checkout</th>
                  </tr>
                </thead>
                <tbody>
                  {p.tiers.map((t) => (
                    <tr key={t.name}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>
                        {t.name}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                        {formatPrice(t.price_cents, t.interval)}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                        {t.paid && t.checkout_path ? (
                          <a
                            href={t.checkout_path}
                            style={{ color: '#2563eb' }}
                            title="Customer-driven Stripe checkout (no autonomous charge)"
                          >
                            Checkout (TEST)
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                <Link to="/omega/marketing" style={{ color: '#2563eb' }}>Marketing</Link>
                <Link to="/omega/revenue" style={{ color: '#2563eb' }}>Revenue</Link>
                <Link to="/omega/chain" style={{ color: '#2563eb' }}>Chain</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
